import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Sparkles, Rocket, X, ExternalLink, ArrowRight, Loader2, ShieldCheck,
} from "lucide-react";import { launchVerticals } from "../api";

/**
 * /launch/designs — the "board" of AI-ready homepage designs.
 * Each mockup card:
 *   - opens a full-size preview modal on click
 *   - has a "Start with this design" CTA that jumps into the /launch wizard
 *     with the vertical preselected via ?vertical={slug}
 *
 * Kept intentionally simple for MVP: static per-vertical mockups from the
 * assets library. In Phase 2 each card links to a real vertical-specific
 * detail page (multi-image gallery, feature callouts, sample copy).
 */
export default function LaunchDesignsPage() {
  const [verticals, setVerticals] = useState(null);
  const [sitesBuilt, setSitesBuilt] = useState([]);
  const [active, setActive] = useState(null); // {slug, ...}
  const navigate = useNavigate();

  useEffect(() => {
    launchVerticals().then(d => {
      setVerticals(d.verticals || []);
      setSitesBuilt(d.sites_built || []);
    });
  }, []);

  const filtered = useMemo(
    () => (verticals || []).filter(v => v.slug !== "generic" && v.mockup_image),
    [verticals]
  );

  const siteForVertical = (slug) =>
    (sitesBuilt || []).find(s => s.vertical === slug);

  return (
    <div className="min-h-screen bg-gradient-to-b from-sky-50 via-white to-white text-slate-900" data-testid="launch-designs-page">
      <div className="max-w-7xl mx-auto px-6 pt-24 pb-16">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <Badge className="bg-sky-100 border border-sky-300 text-sky-800 uppercase tracking-widest text-[10px] font-bold">
              <Sparkles size={11} className="mr-1" /> AI Launch Program · Design Board
            </Badge>
            <h1 className="mt-4 text-4xl sm:text-5xl font-black text-slate-900">Designs we can build for your business</h1>
            <p className="mt-3 text-slate-600 max-w-2xl">
              Tap a design to see it up close. When you like one, we'll adapt it to
              your business — copy, hero image, colors, page plan — and generate
              your free preview in three minutes.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/launch/showcase">
              <Button variant="outline" className="border-slate-300 text-slate-700 bg-white hover:bg-slate-50">
                <ExternalLink size={14} className="mr-1" /> Sites we've built
              </Button>
            </Link>
            <Link to="/launch">
              <Button className="bg-slate-900 hover:bg-slate-800 text-white">
                <ArrowRight size={14} className="mr-1" /> Start my application
              </Button>
            </Link>
          </div>
        </div>

        {!verticals && (
          <div className="mt-16 text-center"><Loader2 className="mx-auto animate-spin text-sky-600" /></div>
        )}

        {verticals && (
          <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {filtered.map((v) => {
              const liveSite = siteForVertical(v.slug);
              return (
              <button key={v.slug} onClick={() => setActive({ ...v, liveSite })}
                data-testid={`design-card-${v.slug}`}
                className="group text-left rounded-2xl overflow-hidden border border-slate-200 bg-white shadow-sm hover:shadow-lg hover:border-sky-400 transition-all relative">
                {liveSite && (
                  <div className="absolute top-3 right-3 z-10 bg-emerald-600 text-white text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded-full flex items-center gap-1 shadow"
                       data-testid={`design-live-badge-${v.slug}`}>
                    <ShieldCheck size={10} /> Live site
                  </div>
                )}
                <div className="aspect-[4/3] overflow-hidden bg-slate-100 relative">
                  <img src={v.mockup_image} alt={`${v.label} — sample design`}
                       className="w-full h-full object-cover object-top group-hover:scale-[1.02] transition-transform duration-500" />
                </div>
                <div className="p-4">
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">
                    {v.slug.replace("_", " ")}
                  </div>
                  <div className="text-slate-900 font-bold text-lg leading-tight mt-1">{v.label}</div>
                  <div className="mt-2 flex items-center justify-between text-xs">
                    <span className="text-slate-500">
                      {v.recommended_pages?.length || 5} pages · {v.ai_demo || "AI assistant"}
                    </span>
                    <span className="text-sky-700 group-hover:text-sky-900 font-bold flex items-center gap-1">
                      Preview <ExternalLink size={11} />
                    </span>
                  </div>
                  {liveSite && (
                    <div className="mt-3 pt-3 border-t border-slate-100 text-xs text-slate-500">
                      → Live example: <span className="font-bold text-slate-800">{liveSite.name}</span>
                    </div>
                  )}
                </div>
              </button>
              );
            })}
          </div>
        )}

        <div className="mt-16 rounded-2xl border border-emerald-200 bg-emerald-50 p-6 sm:p-8 flex flex-wrap gap-6 items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-emerald-800 font-bold flex items-center gap-1">
              <ShieldCheck size={11} /> Every design comes with the Fact Ledger
            </div>
            <div className="mt-1 text-xl font-black text-slate-900">Nothing goes live until you approve every fact.</div>
            <div className="text-sm text-slate-700 mt-1">
              AI drafts the copy — you verify hours, prices, services, credentials before we publish.
            </div>
          </div>
          <Link to="/launch">
            <Button size="lg" className="bg-slate-900 text-white hover:bg-slate-800 font-bold" data-testid="designs-start-cta">
              <Rocket size={16} className="mr-2" /> Get my free AI preview
            </Button>
          </Link>
        </div>
      </div>

      {active && (
        <DesignPreviewModal v={active} onClose={() => setActive(null)}
          onStart={() => { navigate(`/launch?vertical=${active.slug}`); }} />
      )}
    </div>
  );
}

function DesignPreviewModal({ v, onClose, onStart }) {
  return (
    <div className="fixed inset-0 z-[70] bg-slate-900/70 backdrop-blur-sm p-4 flex items-center justify-center overflow-y-auto"
         onClick={onClose} data-testid="design-modal">
      <div className="max-w-5xl w-full bg-white rounded-2xl border border-slate-200 overflow-hidden my-8 shadow-2xl"
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b border-slate-200 bg-slate-50">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-sky-700 font-bold">
              {v.slug.replace("_", " ")} · Sample design
            </div>
            <div className="text-slate-900 font-bold text-lg">{v.label}</div>
          </div>
          <button onClick={onClose} data-testid="design-modal-close"
                  className="text-slate-500 hover:text-slate-900 p-2 rounded-lg hover:bg-slate-100">
            <X size={18} />
          </button>
        </div>
        <div className="p-4 sm:p-6 bg-slate-50">
          <img src={v.mockup_image} alt={`${v.label} full preview`}
               className="w-full h-auto rounded-lg border border-slate-200 shadow" />
        </div>
        <div className="p-4 sm:p-6 border-t border-slate-200 flex flex-wrap items-center justify-between gap-3 bg-white">
          <div className="text-xs text-slate-600 max-w-xl">
            <span className="font-bold text-slate-900">What you'll get:</span>{" "}
            {v.recommended_pages?.join(" · ")} · plus {v.ai_demo}. Preview watermarked,
            editable in your portal, nothing published until you approve every fact.
          </div>
          <div className="flex flex-wrap gap-2">
            {v.liveSite && (
              <Link to={`/launch/showcase/${v.liveSite.slug}`}
                    data-testid={`design-modal-case-study-${v.slug}`}>
                <Button variant="outline" className="border-slate-300 text-slate-700 bg-white hover:bg-slate-50">
                  <ExternalLink size={13} className="mr-1" /> See {v.liveSite.name} case study
                </Button>
              </Link>
            )}
            <Button onClick={onStart} className="bg-slate-900 text-white hover:bg-slate-800 font-bold"
                    data-testid={`design-modal-start-${v.slug}`}>
              <Rocket size={14} className="mr-1" /> Start with this design
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
